/**
 * <sticky-atc> — a sticky add-to-cart bar for the DEFAULT ANIMAL product page.
 *
 * It does NOT own cart logic. It proxies to the real product form rendered by
 * the main product section, so Dawn's product-form.js handles the actual add.
 * Responsibilities:
 *  - reveal on scroll once the main buy box has left the viewport,
 *  - hide again when the newsletter or footer enters the viewport,
 *  - mirror the chosen size and the live variant price,
 *  - block the add and nudge the size grid when no size is chosen,
 *  - reserve its own space (fixed + translateY) so revealing causes no CLS.
 */

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      #controller = new AbortController();
      #observer = null;
      #endObserver = null;
      #buyBoxAnchor = null;
      #buyBoxGone = false;
      #endInView = false;
      #endVisible = new Set();
      #raf = 0;
      #flowBottom = 0;

      connectedCallback() {
        this.#controller = new AbortController();
        // Scope to #MainContent so we never match the cart-drawer upsell form,
        // which renders in <body> before <main> and shares this data-type.
        this.form = document.querySelector('#MainContent form[data-type="add-to-cart-form"]');
        // Nothing to proxy to (e.g. no purchasable variant) — stay hidden.
        if (!this.form) return;

        this.sizeSelector = document.querySelector('size-selector');
        this.addButton = this.querySelector('[data-sticky-add]');
        this.chosenEl = this.querySelector('[data-sticky-chosen]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.variantData = this.#readVariantData();

        this.hidden = false;
        this.style.visibility = 'hidden';

        const { signal } = this.#controller;
        this.addButton?.addEventListener('click', this.#onAdd.bind(this), { signal });

        // Mirror the size grid's selection into the bar.
        document.addEventListener(
          'size-selector:change',
          this.#onSizeChange.bind(this),
          { signal }
        );

        this.#observeBuyBox();
        this.#observePageEnd();
      }

      disconnectedCallback() {
        this.#controller.abort();
        this.#observer?.disconnect();
        this.#endObserver?.disconnect();
        this.#endVisible.clear();
        if (this.#raf) cancelAnimationFrame(this.#raf);
      }

      /** @returns {Record<string, {available: boolean, price: number, compareAt: number|null}>} */
      #readVariantData() {
        const script = this.querySelector('[data-sticky-variants]');
        if (!script) return {};
        try {
          return JSON.parse(script.textContent);
        } catch {
          return {};
        }
      }

      #observeBuyBox() {
        // Reveal once the primary ATC has left the top of the viewport.
        // Using the form's buttons block keeps the trigger tied to the real CTA.
        this.#buyBoxAnchor = this.form.querySelector('.product-form__buttons') || this.form;
        this.#flowBottom = this.#documentBottom(this.#buyBoxAnchor);

        this.#observer = new IntersectionObserver(
          () => {
            this.#syncBuyBoxFromRect();
          },
          { rootMargin: '0px', threshold: 0 }
        );
        this.#observer.observe(this.#buyBoxAnchor);

        // IO only fires on intersection *changes*. The old code also AND-gated
        // on scrollY inside that callback, so further scrolling never re-checked
        // and the dock could stay hidden. Re-read the buy-box rect on scroll.
        const { signal } = this.#controller;
        const onViewport = () => {
          if (this.#raf) return;
          this.#raf = requestAnimationFrame(() => {
            this.#raf = 0;
            this.#syncBuyBoxFromRect();
          });
        };
        window.addEventListener('scroll', onViewport, { passive: true, signal });
        window.addEventListener(
          'resize',
          () => {
            this.#flowBottom = this.#documentBottom(this.#buyBoxAnchor);
            onViewport();
          },
          { passive: true, signal }
        );
        window.addEventListener(
          'load',
          () => {
            this.#flowBottom = this.#documentBottom(this.#buyBoxAnchor);
            this.#syncBuyBoxFromRect();
          },
          { signal }
        );
        this.#syncBuyBoxFromRect();
      }

      /**
       * In-flow document Y of the element's bottom. offsetTop walks the layout
       * box, so a position:sticky buy column does not report its pinned visual
       * Y (which stays on screen until related products).
       * @param {HTMLElement} el
       * @returns {number}
       */
      #documentBottom(el) {
        let y = 0;
        for (let node = el; node; node = node.offsetParent) {
          y += node.offsetTop;
        }
        return y + el.offsetHeight;
      }

      /**
       * True once the main ATC has left the top of the viewport — using the
       * in-flow box so a sticky info column cannot delay the dock until the
       * related-products band. Visual bottom covers the non-sticky (mobile)
       * path. Small hysteresis so the dock does not flicker at the boundary.
       */
      #syncBuyBoxFromRect() {
        const el = this.#buyBoxAnchor;
        if (!el) return;
        const visualBottom = el.getBoundingClientRect().bottom;
        const pastFlow = window.scrollY >= this.#flowBottom;
        const gone = visualBottom <= 0 || pastFlow;
        this.#buyBoxGone = this.#buyBoxGone ? gone || visualBottom <= 48 : gone;
        this.#applyVisibility();
      }

      /**
       * Hide the dock once the newsletter or footer reaches the viewport so
       * it cannot cover the signup field at the bottom of the product page.
       */
      #observePageEnd() {
        const ends = document.querySelectorAll('.signup-bar, footer.footer');
        if (!ends.length) return;

        this.#endObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) this.#endVisible.add(entry.target);
              else this.#endVisible.delete(entry.target);
            }
            this.#endInView = this.#endVisible.size > 0;
            this.#applyVisibility();
          },
          { threshold: 0 }
        );
        for (const el of ends) this.#endObserver.observe(el);
      }

      #applyVisibility() {
        this.#toggle(this.#buyBoxGone && !this.#endInView);
      }

      /** @param {boolean} visible */
      #toggle(visible) {
        this.style.visibility = 'visible';
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('aria-hidden', !visible);
        if (this.addButton) this.addButton.tabIndex = visible ? 0 : -1;
      }

      #onSizeChange(event) {
        const { variantId, size } = event.detail || {};
        const data = this.variantData[String(variantId)] || {};
        if (this.chosenEl && size) {
          this.chosenEl.textContent = size;
        }
        if (variantId) this.#syncPrice(String(variantId));
        this.addButton?.toggleAttribute('aria-disabled', data.available !== true);
      }

      /** @param {string} variantId */
      #syncPrice(variantId) {
        const data = this.variantData[variantId];
        if (!data || !this.priceEl) return;

        const now = this.priceEl.querySelector('.sticky-atc__price-now');
        if (now) now.textContent = this.#money(data.price);

        const wasEl = this.priceEl.querySelector('.sticky-atc__price-was');
        if (data.compareAt && data.compareAt > data.price) {
          if (wasEl) {
            wasEl.textContent = this.#money(data.compareAt);
          } else if (now) {
            const compare = document.createElement('s');
            compare.className = 'sticky-atc__price-was';
            compare.textContent = this.#money(data.compareAt);
            now.before(compare);
          }
        } else if (wasEl) {
          wasEl.remove();
        }
      }

      /**
       * Format cents with the shop's money format if available.
       * @param {number} cents
       * @returns {string}
       */
      #money(cents) {
        const format = window.Shopify?.money_format;
        const amount = (cents / 100).toFixed(2);
        if (typeof format === 'string' && format.includes('{{')) {
          return format.replace(/\{\{\s*amount\s*\}\}/, amount);
        }
        return amount;
      }

      #onAdd(event) {
        event.preventDefault();

        // Guard: force a size choice before adding, reusing the grid's own nudge.
        if (this.sizeSelector && typeof this.sizeSelector.promptForSize === 'function') {
          if (!this.sizeSelector.promptForSize()) return;
        }

        const submit = this.form.querySelector('[type="submit"]');
        if (submit?.getAttribute('aria-disabled') === 'true' || submit?.disabled) return;

        // Fire the real form so Dawn's cart handling (drawer/notification) runs.
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(submit || undefined);
        } else {
          submit?.click();
        }
      }
    }
  );
}
